import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import CourierLayout from '../CourierLayout';
import { API } from '../../../config';

export const CourierUpdatesPanel = ({ embedded = false }) => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);

  const token =
    localStorage.getItem('courier_token') ||
    localStorage.getItem('token');

  const normalizeUpdates = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.notifications)) return data.notifications;
    if (Array.isArray(data?.updates)) return data.updates;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  };

  const fetchUpdates = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API}/api/courier/notifications`, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
      });

      if (!response.ok) {
        setUpdates([]);
        return;
      }

      const data = await response.json();
      setUpdates(normalizeUpdates(data));
    } catch (error) {
      console.error('Failed to fetch courier updates:', error);
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpdates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const content = (
    <Paper
      elevation={embedded ? 1 : 3}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        mb: embedded ? 3 : 0,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        backgroundColor: '#ffffff',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Recent Updates
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Recent load activity, bids, and payouts.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          onClick={fetchUpdates}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Refresh
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            textAlign: 'center',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Loading updates...
          </Typography>
        </Box>
      ) : updates.length === 0 ? (
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            textAlign: 'center',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No updates yet.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {updates.map((update, index) => {
            const title =
              update.title ||
              update.message ||
              update.subject ||
              'Load update';

            const description =
              update.description ||
              update.body ||
              update.details ||
              '';

            return (
              <Box
                key={update.id || update.notification_id || index}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>
                  {title}
                </Typography>

                {description && description !== title && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {description}
                  </Typography>
                )}

                {update.created_at && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    {new Date(update.created_at).toLocaleString()}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );

  if (embedded) {
    return content;
  }

  return <CourierLayout>{content}</CourierLayout>;
};

const CourierNotifications = () => {
  return <CourierUpdatesPanel embedded={false} />;
};

export default CourierNotifications;